import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../../apis/admins';
import { RootState } from '../store';

export interface Admin {
    id: string;
    full_name: string;
    email: string;
    phone_number?: string;
    bio?: string;
    profile_picture_url?: string;
    total_credits?: number;
    deleted_at?: string | null;
    profile?: any;
}

interface AdminState {
    admins: Admin[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: AdminState = {
    admins: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchAdmins = createAsyncThunk(
    'admins/fetchAdmins',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getAdmins(params);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not fetch admins');
            return rejectWithValue(errorText);
        }
    }
);

export const addAdmin = createAsyncThunk(
    'admins/addAdmin',
    async (data: Partial<Admin>, { rejectWithValue }) => {
        try {
            const response = await createAdmin(data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not add admin');
            return rejectWithValue(errorText);
        }
    }
);

export const editAdmin = createAsyncThunk(
    'admins/editAdmin',
    async ({ id, data }: { id: string; data: Partial<Admin> }, { rejectWithValue }) => {
        try {
            const response = await updateAdmin(id, data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not edit admin');
            return rejectWithValue(errorText);
        }
    }
);

export const removeAdmin = createAsyncThunk(
    'admins/removeAdmin',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteAdmin(id);
            return id;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not delete admin');
            return rejectWithValue(errorText);
        }
    }
);

const adminSlice = createSlice({
    name: 'admins',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdmins.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdmins.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.admins = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchAdmins.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(addAdmin.fulfilled, (state, action: PayloadAction<Admin>) => {
                state.admins.push(action.payload);
            })
            .addCase(editAdmin.fulfilled, (state, action: PayloadAction<Admin>) => {
                const index = state.admins.findIndex((t) => t.id === action.payload.id);
                if (index !== -1) state.admins[index] = action.payload;
            })
            .addCase(removeAdmin.fulfilled, (state, action: PayloadAction<string>) => {
                state.admins = state.admins.filter((t) => t.id !== action.payload);
            });
    },
});

export const selectAllAdmins = (state: RootState) => state.admins.admins;
export const selectAdminById = (state: RootState, id: string) =>
    state.admins.admins.find((t) => t.id === id);

export default adminSlice.reducer;
